/**
 * Scheme — how each item is graded.
 *
 * There is no resolved counterpart to these types, unlike content: the scorer
 * takes the document plus a locale and returns verdicts whose messages are
 * already resolved, so nothing else ever needs a half-resolved scheme.
 *
 * The document is self-describing on purpose (D29). A host stores it beside its
 * own task definition, detached from the asset folder, with no `content.json`
 * next to it — so `asset`, `version` and a `type` per item are all required.
 * None of its meaning may depend on a file its holder does not have.
 */

import type { LocalizedText } from './locale.js';
import type { ItemType } from './content.js';

/**
 * Text normalization profiles. Versioned and pinned per attempt, because a
 * later patch must not silently change last term's grades.
 *
 * `ar` is Phase 2, and is where `String.prototype.normalize`'s dependence on the
 * engine's Unicode data version actually bites — alef-hamza and taa-marbuta have
 * composed forms. Hebrew has no composed niqqud to reorder, so `he` is safe now.
 */
export const TEXT_PROFILES = ['he', 'en'] as const;
export type TextProfile = (typeof TEXT_PROFILES)[number];

export interface SchemeDoc {
  readonly $schema?: string;
  /** `manifest.id` of the asset these rules grade. */
  readonly asset: string;
  /** `manifest.version`. The host pins what it stored, so a regrade is reproducible. */
  readonly version: string;
  /** Keyed by content item id, in both directions — enforced by a contract rule. */
  readonly items: { readonly [itemId: string]: ItemSchemeDoc };
  readonly aggregate?: AggregateDoc;
}

/** Whether a partly-right sequence earns part of the points. */
export const CREDIT_RULES = ['all', 'partial'] as const;
export type Credit = (typeof CREDIT_RULES)[number];

/**
 * How a multi-select is marked. A superset of `CREDIT_RULES`, because "select all that apply"
 * is the one type where the field has a real choice to make and the established systems disagree
 * about it.
 *
 * The two families that matter, and which real systems sit in which:
 *
 *   `all`        Exact set match or nothing. QTI's `match_correct` response-processing template,
 *                and the default in Blackboard's Multiple Answer question. **The default here
 *                too**, because it is the most common default in the tools CET authors already
 *                use, and because it is the only rule that needs no judgement about how to
 *                weigh a wrong tick.
 *
 *   `partial`    Right ticks earn, wrong ticks deduct, floored at zero. This is where the LMSs
 *                converge: QTI's `map_response` (per-option weights, negatives for distractors,
 *                summed and bounded below), Moodle's multiple-response grading (positive
 *                fractions for correct options, negative for incorrect), and Canvas's Multiple
 *                Answers partial credit are all this shape. Implemented as
 *                `max(0, hits − misses) ÷ correctCount`, which is the simplest member of the
 *                family and needs nothing the scheme does not already carry.
 *
 *   `perOption`  Every option is an independent right-or-wrong decision, scored
 *                `rightDecisions ÷ optionCount` — where leaving a distractor alone *counts as a
 *                right decision*. This is not the multi-select rule; it is the standard rule for
 *                **multiple true-false** items, which is a different item genuinely worth having.
 *                Choose it when an unticked box means "no" rather than "not answered". It needs
 *                `optionCount`, and its guessing floor is high by construction — see the field.
 *
 * `perOption` is deliberately a third value rather than a second boolean: the three are mutually
 * exclusive readings of the same answer, and a flag beside `credit` would allow combinations that
 * mean nothing.
 */
export const MULTI_CHOICE_CREDIT_RULES = ['all', 'partial', 'perOption'] as const;
export type MultiChoiceCredit = (typeof MULTI_CHOICE_CREDIT_RULES)[number];

export type ItemSchemeDoc =
  | ChoiceSchemeDoc
  | MultiChoiceSchemeDoc
  | TextEntrySchemeDoc
  | OrderingSchemeDoc
  | MatchingSchemeDoc
  | NumericSchemeDoc;

export interface ChoiceSchemeDoc {
  readonly type: 'choice';
  /** The id of the correct choice. This value is why the file is never published. */
  readonly correct: string;
  readonly points: number;
  readonly feedback?: FeedbackDoc;
  readonly hints?: readonly HintDoc[];
}

export interface MultiChoiceSchemeDoc {
  readonly type: 'multiChoice';
  /**
   * Every choice id that should be ticked, in any order — compared as a set. Must be a
   * non-empty subset of the item's choices, and must not be *all* of them, or ticking
   * everything is full marks. A contract rule checks both.
   *
   * One correct id is legitimate: a single answer among checkboxes asks the learner to
   * discover that only one applies, which a radio group gives away. Under the default
   * `credit` that item grades identically to a `choice` item.
   */
  readonly correct: readonly string[];
  readonly points: number;
  /**
   * Defaults to `all`. See MULTI_CHOICE_CREDIT_RULES for which real systems use which, and why
   * `all` is the default; the arithmetic of each is in `items/multi-choice.ts`.
   *
   * The one thing worth knowing before choosing `partial`: **a wrong tick has to cost
   * something.** The obvious alternative, `hits ÷ correctCount`, pays *full marks* for ticking
   * every box, so the highest-scoring strategy would be to know nothing. Every system in the
   * `partial` family deducts for exactly this reason. HendrX scores `right ÷ (correct + wrong)`
   * and needs a separate all-selected guard on top; deducting removes the need for the guard.
   */
  readonly credit?: MultiChoiceCredit;
  /**
   * How many choices the item shows in total — correct ones and distractors together.
   *
   * **Required by `credit: "perOption"` and ignored by the others.** That rule has to know how
   * many boxes were *not* ticked in order to credit them, and the scorer never sees
   * `content.json` (D5), so the count cannot be derived. A contract rule checks it against the
   * content's actual choice count in both directions, so it cannot drift.
   *
   * A count rather than the list of ids, because the count is all the arithmetic needs and it is
   * just as checkable. `ordering.correct` restates the content's ids because its *comparison*
   * needs them; nothing here does.
   *
   * **The floor this rule accepts, stated plainly, because it is the reason it is not the
   * default.** Leaving a distractor alone counts as a right decision, so a learner who ticks one
   * wrong box and nothing else still scores `(optionCount − 3) ÷ optionCount` — 40% on a
   * five-option item and **75% on a twelve-option one**. Adding distractors therefore makes an
   * item *easier* to score well on, which is backwards. That is tolerable for a multiple
   * true-false item, where each statement is a real question and a coin-flip floor is expected,
   * and it is grade inflation anywhere else. Keep `optionCount` small when using it.
   */
  readonly optionCount?: number;
  readonly feedback?: FeedbackDoc;
  readonly hints?: readonly HintDoc[];
}

export interface TextEntrySchemeDoc {
  readonly type: 'textEntry';
  /**
   * Compared after normalization by `text`. Exact match only in Phase 1. A match earns full points
   * at once, judge or no judge; empty only when `judge` is set (D93).
   */
  readonly accept: readonly string[];
  readonly text: TextProfile;
  readonly points: number;
  /** Any other answer is judged by the AI against this (D93). Absent: an exact match or nothing. */
  readonly judge?: JudgeDoc;
  readonly feedback?: FeedbackDoc;
  readonly hints?: readonly HintDoc[];
}

/**
 * What the AI judges an open answer against (D93). In the scheme, so it is never published, and
 * the judging server reads it from its own copy — the page sends only the answer.
 */
export interface JudgeDoc {
  /** The task as the AI should read it. Absent: the item's prompt. */
  readonly task?: string;
  /** What a right answer contains, in plain words — never how well it is written. */
  readonly criteria: string;
  /** One right answer, written out. Shown as the solution when `accept` is empty. */
  readonly model?: string;
  /**
   * Points by criterion, summing to the item's `points`. Absent, or without score tables: the AI's
   * pass is full points and its fail is none. With a `levels` table on every criterion the AI picks a
   * level for each, and this server adds up their points (D110).
   */
  readonly rubric?: readonly RubricDoc[];
  /** How to judge, in plain sentences, for the AI: what counts, what does not (D110). Told, not enforced. */
  readonly rules?: readonly string[];
  /** What the item's material shows, for the AI to check an answer against: it does not see a picture (D110). */
  readonly facts?: readonly string[];
  /** Answers already marked, for the AI to calibrate against: a teacher's guide's examples. */
  readonly examples?: readonly JudgeExampleDoc[];
  /** Words the answer must use. */
  readonly mustUse?: readonly string[];
  /** How long the answer should be, in words a teacher would say: "one or two sentences". */
  readonly shape?: string;
  /** The learner's school grade, 1–12, for the language the feedback uses. */
  readonly grade?: number;
}

export interface RubricDoc {
  /** Unique within the item. */
  readonly id: string;
  readonly criterion: string;
  /** May be 0: a criterion that only caps the total or raises a flag (D110). */
  readonly points: number;
  /**
   * The criterion's score table, 2–8 levels: what an answer at each level shows, as a teacher's guide
   * gives it. The AI picks one; its `score` is the criterion's points (D110).
   */
  readonly levels?: readonly RubricLevelDoc[];
  /** A score of 0 here makes the whole answer 0 — a teacher's guide's "semantic gate" (D110). */
  readonly gate?: boolean;
}

export interface RubricLevelDoc {
  /** Unique within the criterion: what the AI answers with, e.g. "10" or "one". */
  readonly level: string;
  /** 0 to the criterion's points. */
  readonly score: number;
  readonly descriptor: string;
  /** The answer's total is at most this when the AI picks this level (D110): "one detail caps it at 10". */
  readonly cap?: number;
  /** The note this level raises for a teacher (D110). */
  readonly flag?: JudgeFlag;
}

/** Whether the AI judges an item by levels (D110): a rubric, and a score table on every criterion. */
export function isLevelled(judge: JudgeDoc): boolean {
  return (judge.rubric?.length ?? 0) > 0 && (judge.rubric ?? []).every((criterion) => (criterion.levels?.length ?? 0) > 0);
}

export interface JudgeExampleDoc {
  readonly answer: string;
  /** On the item's own scale: 0 to its points. */
  readonly points: number;
  /** By rubric criterion id, when the item has a rubric (D110). */
  readonly scores?: { readonly [criterionId: string]: number };
  /** The notes the AI should raise on this answer (D110). */
  readonly flags?: readonly JudgeFlag[];
  readonly why?: string;
}

/**
 * A note the AI raises on an answer for a teacher, beside its score (D110): `creative_intent` — an
 * unexpected answer in strong language, scored on its language and not failed; `low_confidence` —
 * the AI is unsure (unclear speech, borderline meaning), so a person should look.
 */
export const JUDGE_FLAGS = ['creative_intent', 'low_confidence'] as const;
export type JudgeFlag = (typeof JUDGE_FLAGS)[number];

/**
 * Whether an item's answers, beyond its exact ones, are judged by the AI (D93). Not one worth no
 * points (D108): its answer is only recorded, and a judgement could change nothing in the score.
 */
export function isJudged(item: ItemSchemeDoc | undefined): item is TextEntrySchemeDoc & { readonly judge: JudgeDoc } {
  return item?.type === 'textEntry' && item.judge !== undefined && item.points > 0;
}

/**
 * How a typed number is marked: an inclusive interval, per entry field.
 *
 * **Bounds, not `equals` + `tolerance`, and this is the whole design.** The obvious shape is
 * "the answer is 7.5, accept ±0.05", marked as `|given − equals| <= tolerance`. That form marks
 * correct answers wrong, silently, for arithmetic reasons no author can see:
 *
 *   1.0  ± 0.1   a learner types 1.1   |d| = 0.10000000000000009   → wrong
 *   4.35 ± 0.05  a learner types 4.4   |d| = 0.05000000000000071   → wrong
 *
 * Deriving the bounds at run time instead — `equals - tolerance` — fixes the first and not the
 * second. What fixes both is doing **no arithmetic at all**: two comparisons against literals the
 * author wrote, where `4.4 <= 4.4` is exact because both sides are the same double. So a
 * tolerance is written out as bounds, and the scorer only ever compares.
 *
 * Range, tolerance and exact answer are then one comparison rather than three concepts. An exact
 * answer is `min === max`.
 */
export interface NumericSchemeDoc {
  readonly type: 'numeric';
  /** Inclusive lower bound, authored as a literal. */
  readonly min: number;
  /** Inclusive upper bound, authored as a literal. Must not be below `min`. */
  readonly max: number;
  /**
   * Must equal the content item's `fields`, defaulting to 1 in both files.
   *
   * Duplicated for the same reason as `multiChoice.optionCount`: the scorer never sees the
   * content (D5), and `credit: "partial"` needs the denominator — a learner who fills one of
   * four boxes correctly must score one quarter, not full marks. A contract rule checks it
   * against the content in both directions, so it cannot drift.
   */
  readonly fields?: number;
  /**
   * "Four **different** numbers between 2.2 and 3.1" — the values must differ from one another.
   *
   * Compared as parsed numbers, so `2.20` and `2.2` are the same answer and the second is a
   * repeat. The first occurrence keeps its credit and a later duplicate is wrong, which is
   * deterministic because field order is the order the asset drew them.
   *
   * This is the field that decides whether a question is **one item with N fields or N items
   * with one field each**: a constraint across fields can only be expressed inside one item.
   * Without `distinct`, the two arrangements grade identically and the choice is about how many
   * rows the gradebook should have.
   */
  readonly distinct?: boolean;
  /**
   * `all` (the default) scores nothing unless every field is right. `partial` awards
   * `floor(points × validFields ÷ fields)` — integer arithmetic, as everywhere else, so three
   * of four boxes right is not a zero.
   */
  readonly credit?: Credit;
  /**
   * An example correct answer, one string per field — what solution mode shows.
   *
   * **This type is the first whose answer is not unique**, and that is why the field exists. A
   * range has infinitely many correct answers, so `solutionFor` has nothing to derive; the
   * honest fallback, repeating `min` in every field, is *itself wrong* under `distinct`, which
   * would make solution mode display an answer that scores one quarter. So a contract rule
   * requires this whenever the answer is not unique — anything but `min === max` with a single
   * field — and the fallback exists only so the type stays honest.
   *
   * `solution.ts` anticipated exactly this as the `display` field it would need if `textEntry`
   * ever had to say which accepted string is canonical.
   */
  readonly display?: readonly string[];
  readonly points: number;
  readonly feedback?: FeedbackDoc;
  readonly hints?: readonly HintDoc[];
}

export interface OrderingSchemeDoc {
  readonly type: 'ordering';
  /**
   * The correct sequence of choice ids. Must be a permutation of the item's choices —
   * every id exactly once — and must differ from the order the content displays, or the
   * answer is visible in the published file. A contract rule checks both.
   */
  readonly correct: readonly string[];
  readonly points: number;
  /**
   * `all` (the default) scores nothing unless the whole sequence is right. `partial`
   * awards `floor(points × correctPositions ÷ length)` — integer arithmetic, because
   * scores never accumulate as floats, and three of four positions right should not be
   * zero.
   */
  readonly credit?: Credit;
  readonly feedback?: FeedbackDoc;
  readonly hints?: readonly HintDoc[];
}

export interface MatchingSchemeDoc {
  readonly type: 'matching';
  /**
   * Which right-hand id each left-hand id belongs with. **Every left id must appear as a
   * key** — a left with no correct answer is unanswerable — and every value must be a
   * right id. A contract rule checks both, and also that the mapping is not simply the two
   * lists lined up index by index, which would put the answer in the published content.
   *
   * Values may repeat: several lefts belonging to one right is a classification activity.
   */
  readonly correct: { readonly [leftId: string]: string };
  readonly points: number;
  /**
   * `all` (the default) scores nothing unless every pair is right. `partial` awards
   * `floor(points × correctPairs ÷ pairs)`, which is what the old Dot2Dot did — it scored
   * `correct × 100 ÷ total` and reported a per-pair mark — and matching is the type where
   * all-or-nothing is least defensible, since six pairs with one crossed is not zero
   * understanding.
   */
  readonly credit?: Credit;
  readonly feedback?: FeedbackDoc;
  readonly hints?: readonly HintDoc[];
}

/**
 * Feedback text lives here rather than in the content, because a message
 * written for the right answer identifies it (D21). Withheld with the rest of
 * the scheme in exam mode — correctly, since there is no feedback in exam mode
 * to write text for.
 */
export interface FeedbackDoc {
  readonly correct?: LocalizedText;
  readonly incorrect?: LocalizedText;
}

/**
 * Help offered *before* an answer, as opposed to feedback offered after it.
 *
 * It sits in the scheme for one reason, and it is the whole of D48: **the scheme is the file
 * a host withholds.** `init.scheme: null` is already the exam guarantee, stated once, and
 * hanging help off it means exam mode withholds hints by construction rather than by a second
 * rule someone has to remember. Content is published in every mode, so a hint in
 * `content.json` is a hint in an exam, and an asset cannot fix that on its own: branching on
 * `app.mode` is rejected, and `feedbackMode` answers a different question — `practice` with
 * `onSubmit` is a real arrangement, so it is not a proxy for "help is allowed".
 *
 * The `id` is what the reveal reports. Text would be worse in three ways at once: it changes
 * with the locale, it changes when an author rewords, and it cannot be counted.
 */
export interface HintDoc {
  /** Unique within the item. Stable across translations and rewordings. */
  readonly id: string;
  readonly text: LocalizedText;
}

export interface AggregateDoc {
  readonly method: 'sum';
  /**
   * Integer percent, not a fraction. Compared as `raw * 100 >= passing * max`,
   * which is exact integer arithmetic — scores never accumulate as floats.
   */
  readonly passing?: number;
  /**
   * Named parts of the activity, each a list of item ids, scored apart beside the total — a
   * placement test's levels (D93). What a part's score means is the host's to decide.
   */
  readonly parts?: { readonly [partId: string]: readonly string[] };
}

/**
 * The scheme item's declared type must equal the content item's. Stated here as
 * a function rather than left to the validator so both sides use one definition
 * of agreement.
 */
export function schemeTypeMatchesItemType(scheme: ItemSchemeDoc, itemType: ItemType): boolean {
  return scheme.type === itemType;
}
